import type { Asset } from '@odo/shared-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/config';
import { reportError } from '../services/errorReporting';

interface GcsPatientFile {
    id: string;
    file: {
        originalFilename: string;
        mimeType: string;
        createdAt: string;
    };
    /** Signed thumbnail read URL for image files; null otherwise. May 404 for not-yet-backfilled images. */
    thumbUrl?: string | null;
    /** Coarse storage category (PatientFileType, e.g. CBCT, STL). */
    fileType?: string | null;
    /** Free-form metadata (e.g. CBCT manifest/binary viewer refs). */
    metadata?: Record<string, unknown> | null;
}

interface UsePatientAssetsOptions {
    skip?: boolean;
}

interface UsePatientAssetsReturn {
    assets: Record<string, Asset>;
    isLoading: boolean;
    fetchError: string | null;
    refreshAssets: () => Promise<void>;
}

/** GCS is the sole source for patient files, including chat uploads. */
const toAssetMap = (gcsData: GcsPatientFile[]): Record<string, Asset> => {
    const gcsAssets: Record<string, Asset> = {};
    gcsData.forEach((patientFile) => {
        const file = patientFile.file;
        const assetId = `gcs-${patientFile.id}`;
        gcsAssets[assetId] = {
            name: file.originalFilename,
            assetId,
            fileType: file.mimeType,
            publicUrl: '',
            createdAt: file.createdAt,
            source: 'gcs' as const,
            patientFileId: patientFile.id,
            thumbUrl: patientFile.thumbUrl ?? null,
            storageCategory: patientFile.fileType ?? null,
            metadata: patientFile.metadata ?? null
        };
    });

    return gcsAssets;
};

export const usePatientAssets = (
    patientId: string | null | undefined,
    options?: UsePatientAssetsOptions
): UsePatientAssetsReturn => {
    const [assets, setAssets] = useState<Record<string, Asset>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const hasFetched = useRef(false);
    const skip = options?.skip ?? false;

    const refreshAssets = useCallback(async (): Promise<void> => {
        if (!patientId) return;
        setFetchError(null);
        try {
            const gcsResponse = await api.get<GcsPatientFile[]>(
                `/patients/${patientId}/cloud-files`
            );
            setAssets(toAssetMap(gcsResponse.data));
        } catch (error) {
            reportError({
                source: 'dashboard',
                message: `Failed to load patient files: ${error instanceof Error ? error.message : String(error)}`,
                stack: error instanceof Error ? error.stack : undefined,
                url: window.location.href,
                pathname: window.location.pathname,
                userAgent: navigator.userAgent,
                errorType: 'unhandledrejection',
                metadata: { patientId, context: 'cloud_files_fetch' }
            });
            setFetchError('Failed to load files. Please try again.');
        }
    }, [patientId]);

    useEffect(() => {
        if (skip || !patientId || hasFetched.current) return;
        hasFetched.current = true;

        const fetchAssets = async () => {
            setIsLoading(true);
            try {
                await refreshAssets();
            } finally {
                setIsLoading(false);
            }
        };
        fetchAssets();
    }, [patientId, skip, refreshAssets]);

    // Reset when skip transitions from true to false
    useEffect(() => {
        if (!skip) {
            hasFetched.current = false;
        }
    }, [skip]);

    return { assets, isLoading, fetchError, refreshAssets };
};
